// Funnel steps for PostHog. The funnel bridge and the assessment template push
// plain `{ event: 'borg_*' }` objects onto dataLayer; Google Ads takes the few
// that are conversions (conversions.ts), PostHog takes all of them so the
// funnel can be read step by step: landing, test started, each question,
// finished, qualified or waitlist, booked.
//
// Privacy: only the event name and a whitelist of non-personal properties
// leave the page. Never an email, name, phone or free text. Answers leave only
// as the fixed option labels of spend and team size, plus a tool count.

// Stamped on every PostHog event this site sends (pageviews included), so Borg
// data is filterable as product = borg and never mixes with Search Atlas, even
// in a shared project. Funnel event names also all start with borg_.
export const BORG_PROPS = { product: 'borg', site: 'getborg.com' } as const

const ALLOWED_PROPS = ['step', 'total', 'variant', 'placement', 'lander', 'cta', 'depth', 'question', 'qualified', 'spend_band', 'team_band', 'tool_count', 'code', 'path'] as const
type FunnelProps = Partial<Record<(typeof ALLOWED_PROPS)[number], string | number | boolean>>

export function funnelEvent(entry: unknown): { name: string; props: FunnelProps } | undefined {
  if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) return undefined
  // gtag queues Arguments objects on the same array; those are tag traffic.
  if (Object.prototype.toString.call(entry) === '[object Arguments]') return undefined
  const e = entry as Record<string, unknown>
  if (typeof e.event !== 'string' || !/^borg_[a-z_]+$/.test(e.event)) return undefined
  const props: FunnelProps = {}
  for (const key of ALLOWED_PROPS) {
    const v = e[key]
    if ((typeof v === 'string' && v.length <= 40) || (typeof v === 'number' && Number.isFinite(v)) || typeof v === 'boolean') props[key] = v
  }
  return { name: e.event, props }
}

// Calls `send` once for every funnel event already queued and every one pushed
// later. Defines push rather than assigning it, like conversions.ts, so the
// array keeps its shape for anything else iterating dataLayer.
export function watchFunnelEvents(dataLayer: unknown[], send: (name: string, props: FunnelProps) => void): void {
  const report = (entry: unknown) => {
    const ev = funnelEvent(entry)
    if (ev) send(ev.name, ev.props)
  }
  for (const entry of [...dataLayer]) report(entry)
  const push = dataLayer.push.bind(dataLayer)
  Object.defineProperty(dataLayer, 'push', {
    configurable: true,
    writable: true,
    enumerable: false,
    value: (...entries: unknown[]) => {
      const length = push(...entries)
      for (const entry of entries) report(entry)
      return length
    },
  })
}

// Where this visit came from, without personal data: utm values from the
// landing URL (kept for the session, since later pages carry no query) and
// whether a Google Ads or Meta Ads click id was present. Click ids never leave.
export interface TrafficSource { utm_source?: string; utm_medium?: string; utm_campaign?: string; from_google_ads: boolean; from_meta_ads: boolean }
const KEY = 'borg-traffic'
const clean = (value: unknown) => {
  const v = typeof value === 'string' ? value.trim() : ''
  return v && v.length <= 100 && /^[\w .|:+-]+$/.test(v) ? v : undefined
}

export function trafficSource(search: string, storage: Pick<Storage, 'getItem' | 'setItem'> | undefined): TrafficSource {
  const q = new URLSearchParams(search)
  const now: TrafficSource = {
    utm_source: clean(q.get('utm_source')),
    utm_medium: clean(q.get('utm_medium')),
    utm_campaign: clean(q.get('utm_campaign')),
    from_google_ads: q.has('gclid') || q.has('gbraid') || q.has('wbraid'),
    from_meta_ads: q.has('fbclid'),
  }
  const landed = now.utm_source || now.utm_medium || now.utm_campaign || now.from_google_ads || now.from_meta_ads
  try {
    if (landed) {
      storage?.setItem(KEY, JSON.stringify(now))
      return now
    }
    const saved = JSON.parse(storage?.getItem(KEY) ?? 'null') as TrafficSource | null
    if (saved && typeof saved === 'object' && !Array.isArray(saved)) return { utm_source: clean(saved.utm_source), utm_medium: clean(saved.utm_medium), utm_campaign: clean(saved.utm_campaign), from_google_ads: saved.from_google_ads === true, from_meta_ads: saved.from_meta_ads === true }
  } catch {
    // Storage blocked: report this page's own source only.
  }
  return now
}

// Which lander started this visit's funnel: set on the lander itself, then read
// back on the assessment, results and booking pages of the same tab session.
export const FUNNEL_LANDERS = { '/lp/': 'a', '/lp/b/': 'b', '/lp/c/': 'c', '/lp/competitor/': 'competitor', '/lp/team/': 'team' } as const
export type FunnelLander = (typeof FUNNEL_LANDERS)[keyof typeof FUNNEL_LANDERS]
const LANDER_KEY = 'borg-lander'
export const isFunnelLander = (v: unknown): v is FunnelLander => Object.values(FUNNEL_LANDERS).some(x => x === v)

export function entryLander(pathname: string, storage: (Pick<Storage, 'getItem' | 'setItem'> & Partial<Pick<Storage, 'removeItem'>>) | undefined): FunnelLander | undefined {
  const here = (FUNNEL_LANDERS as Record<string, FunnelLander>)[pathname.endsWith('/') ? pathname : `${pathname}/`]
  try {
    if (here) {
      storage?.setItem(LANDER_KEY, here)
      // The /lp/ tests run on lander a only; arriving on another lander in the
      // same tab must not keep reporting an arm assigned on an earlier /lp/ visit.
      if (here !== 'a') for (const key of ['borg-headline', 'borg-hero']) storage?.removeItem?.(key)
      return here
    }
    const saved = storage?.getItem(LANDER_KEY)
    return isFunnelLander(saved) ? saved : undefined
  } catch {
    return here
  }
}

// Test arms assigned on /lp/ (build-borg-funnel.mjs HEADLINE_TEST, HERO_TEST),
// kept for this tab session. Only a short lowercase key is ever read back.
export const isTestArm = (v: unknown): v is string => typeof v === 'string' && /^[a-z]{1,20}$/.test(v)
export function entryArm(storage: Pick<Storage, 'getItem'> | undefined, key: 'borg-headline' | 'borg-hero'): string | undefined {
  try {
    const saved = storage?.getItem(key)
    return isTestArm(saved) ? saved : undefined
  } catch {
    return undefined
  }
}
